import type {AhpDebugEvent} from '@github-ui/copilot-shared-ui/utils/ahp-debug-log'

const RANGE_CONTEXT_LINES = 5

export interface DebugLogSummary {
  rawLine: number
  timestamp: string
  source: string
  direction: string
  method?: string
  actionType?: string
  turnId?: string
  toolCallId?: string
  actionId?: string
  channel?: string
  pendingKind?: string
  requestKind?: string
  requestId?: string
  rejected: boolean
  isError: boolean
  text: string
}

export interface DebugLogDiagnostic {
  kind: string
  message: string
  line?: number
}

export interface DebugLogSuggestedRange {
  start_line: number
  end_line: number
  reason: string
}

export function summarizeDebugEvent(event: AhpDebugEvent, rawLine: number): DebugLogSummary | undefined {
  let payload: unknown
  try {
    payload = JSON.parse(event.message)
  } catch {
    return undefined
  }
  if (!payload || typeof payload !== 'object') return undefined
  const objectPayload = payload as Record<string, unknown>
  const transport = summarizeTransport(objectPayload, event, rawLine)
  if (transport) return transport

  const data = objectPayload.data
  const message = data && typeof data === 'object' && 'data' in data ? (data as {data?: unknown}).data : data
  if (!message || typeof message !== 'object') return undefined
  const rpc = message as Record<string, unknown>
  if (rpc.method === 'initialize') {
    const params = asRecord(rpc.params)
    return createSummary(event, rawLine, {
      method: 'initialize',
      actionType: 'initialize',
      text: joinSummaryFields(['initialize', stringField(params, 'channel') && `channel=${params.channel}`]),
    })
  }
  if (rpc.method !== 'dispatchAction' && rpc.method !== 'action') return undefined

  const params = asRecord(rpc.params)
  const action = asRecord(params.action)
  const actionType = stringField(action, 'type')
  if (!actionType) return undefined
  const request = asRecord(action.request)
  const requestToolCall = asRecord(request.toolCall)
  const rejection = compact(stringField(params, 'rejectionReason'))
  const error = compact(errorMessage(action))
  const turnId = stringField(action, 'turnId') ?? stringField(request, 'turnId')
  const toolCallId = stringField(action, 'toolCallId') ?? stringField(requestToolCall, 'toolCallId')
  const actionId = stringField(action, 'id')
  const requestId = stringField(request, 'id')
  const messageText = compact(stringField(asRecord(action.message), 'text'))
  const fields = [
    actionType,
    stringField(params, 'channel') && `channel=${params.channel}`,
    stringField(action, 'kind') && `kind=${action.kind}`,
    turnId && `turn=${turnId}`,
    toolCallId && `tool=${toolCallId}`,
    (actionId ?? requestId) && `id=${actionId ?? requestId}`,
    messageText && `text=${JSON.stringify(messageText)}`,
    stringField(request, 'kind') && `request=${request.kind}`,
    rejection && `rejection=${JSON.stringify(rejection)}`,
    error && `error=${JSON.stringify(error)}`,
    numberField(params, 'serverSeq') !== undefined && `serverSeq=${params.serverSeq}`,
    numberField(asRecord(params.origin), 'clientSeq') !== undefined && `clientSeq=${asRecord(params.origin).clientSeq}`,
  ]

  return createSummary(event, rawLine, {
    method: rpc.method,
    actionType,
    turnId,
    toolCallId,
    actionId,
    requestId,
    channel: stringField(params, 'channel'),
    pendingKind: stringField(action, 'kind'),
    requestKind: stringField(request, 'kind'),
    rejected: Boolean(rejection),
    isError: Boolean(rejection || error || /error|failed|timeout|cancelled/i.test(actionType)),
    text: joinSummaryFields(fields),
  })
}

function errorMessage(action: Record<string, unknown>): string | undefined {
  const actionError = action.error
  if (typeof actionError === 'string') return actionError
  const messageError = asRecord(action.message).error
  if (typeof messageError === 'string') return messageError
  return (
    stringField(asRecord(actionError), 'message') ??
    stringField(asRecord(messageError), 'message') ??
    stringField(asRecord(action._meta), 'error')
  )
}

function summarizeTransport(
  payload: Record<string, unknown>,
  event: AhpDebugEvent,
  rawLine: number,
): DebugLogSummary | undefined {
  if (payload.type === 'ack') {
    const failed = payload.success === false
    return createSummary(event, rawLine, {
      method: 'ack',
      actionType: 'ack',
      isError: failed,
      text: joinSummaryFields([
        'ack',
        payload.ackId !== undefined && `ack=${String(payload.ackId)}`,
        failed ? 'success=false' : 'success=true',
      ]),
    })
  }
  if (payload.type === 'system') {
    const actionType = stringField(payload, 'event') ?? 'system'
    return createSummary(event, rawLine, {
      method: actionType,
      actionType,
      text: joinSummaryFields([
        actionType,
        stringField(payload, 'connectionId') && `connection=${payload.connectionId}`,
      ]),
    })
  }
  return undefined
}

function createSummary(
  event: AhpDebugEvent,
  rawLine: number,
  values: Partial<DebugLogSummary> & Pick<DebugLogSummary, 'text'>,
): DebugLogSummary {
  return {
    rawLine,
    timestamp: event.timestamp,
    source: event.source,
    direction: event.direction,
    rejected: false,
    isError: false,
    ...values,
    text: [event.timestamp, `${event.source}/${event.direction}`, values.text].join(' | '),
  }
}

export function buildDebugLogDiagnostics(summaries: readonly DebugLogSummary[]): DebugLogDiagnostic[] {
  const diagnostics: DebugLogDiagnostic[] = []
  const turns = new Map<string, {line: number; started: boolean; completed: boolean}>()
  const tools = new Map<string, {line: number; turnId?: string; starts: number; readies: number; completed: boolean}>()
  const pendingMessages = new Map<string, {line: number; description: string}>()
  const inputRequests = new Map<string, {line: number; description: string}>()

  for (const summary of summaries) {
    if (summary.isError) diagnostics.push({kind: 'notable_event', message: summary.text, line: summary.rawLine})
    if (summary.rejected) continue

    if (summary.turnId) {
      const turn = turns.get(summary.turnId) ?? {line: summary.rawLine, started: false, completed: false}
      if (summary.actionType === 'chat/turnStarted') {
        turn.line = summary.rawLine
        turn.started = true
      }
      if (/^chat\/(turnComplete|turnCancelled|error)$/.test(summary.actionType ?? '')) turn.completed = true
      turns.set(summary.turnId, turn)
    }
    if (summary.toolCallId) {
      const tool = tools.get(summary.toolCallId) ?? {
        line: summary.rawLine,
        turnId: summary.turnId,
        starts: 0,
        readies: 0,
        completed: false,
      }
      if (summary.actionType === 'chat/toolCallStart') tool.starts += 1
      if (summary.actionType === 'chat/toolCallReady') tool.readies += 1
      if (summary.actionType === 'chat/toolCallComplete') tool.completed = true
      tools.set(summary.toolCallId, tool)
    }
    recordPendingLifecycle(summary, pendingMessages, inputRequests, diagnostics)
  }

  for (const [turnId, turn] of turns) {
    if (turn.started && !turn.completed) {
      diagnostics.push({kind: 'open_turn', message: `Open turn at end of log: ${turnId}`, line: turn.line})
    }
  }
  for (const [toolCallId, tool] of tools) {
    if (tool.starts > 1) {
      diagnostics.push({
        kind: 'duplicate_tool_start',
        message: `Duplicate toolCallStart: ${toolCallId} (${tool.starts} starts)`,
        line: tool.line,
      })
    }
    if (tool.readies > 1) {
      diagnostics.push({
        kind: 'duplicate_tool_ready',
        message: `Duplicate toolCallReady: ${toolCallId} (${tool.readies} ready events)`,
        line: tool.line,
      })
    }
    if (tool.starts > 0 && !tool.completed) {
      diagnostics.push({
        kind: 'open_tool',
        message: `Open tool at end of log: ${toolCallId}${tool.turnId ? ` (turn ${tool.turnId})` : ''}`,
        line: tool.line,
      })
    }
  }
  for (const pending of pendingMessages.values()) {
    diagnostics.push({kind: 'open_pending_message', message: pending.description, line: pending.line})
  }
  for (const request of inputRequests.values()) {
    diagnostics.push({kind: 'open_input_request', message: request.description, line: request.line})
  }

  return diagnostics
}

function recordPendingLifecycle(
  summary: DebugLogSummary,
  pendingMessages: Map<string, {line: number; description: string}>,
  inputRequests: Map<string, {line: number; description: string}>,
  diagnostics: DebugLogDiagnostic[],
): void {
  if (summary.method !== 'action' || !summary.channel) return
  if (summary.actionType === 'chat/pendingMessageSet' && summary.pendingKind && summary.actionId) {
    pendingMessages.set(`${summary.channel}\0${summary.pendingKind}\0${summary.actionId}`, {
      line: summary.rawLine,
      description: `Open pending message: ${summary.pendingKind} ${summary.actionId}`,
    })
  } else if (summary.actionType === 'chat/pendingMessageRemoved' && summary.pendingKind && summary.actionId) {
    const key = `${summary.channel}\0${summary.pendingKind}\0${summary.actionId}`
    if (!pendingMessages.delete(key)) {
      diagnostics.push({
        kind: 'unmatched_pending_removal',
        message: `Pending-message removal without matching set: ${summary.pendingKind} ${summary.actionId}`,
        line: summary.rawLine,
      })
    }
  }

  const inputId = summary.actionType === 'session/inputNeededSet' ? summary.requestId : summary.actionId
  if (!inputId) return
  const inputKey = `${summary.channel}\0${inputId}`
  if (summary.actionType === 'session/inputNeededSet') {
    inputRequests.set(inputKey, {
      line: summary.rawLine,
      description: `Open input request: ${inputId}${summary.requestKind ? ` (${summary.requestKind})` : ''}`,
    })
  } else if (summary.actionType === 'session/inputNeededRemoved' && !inputRequests.delete(inputKey)) {
    diagnostics.push({
      kind: 'unmatched_input_removal',
      message: `Input-request removal without matching set: ${inputId}`,
      line: summary.rawLine,
    })
  }
}

export function buildDebugLogSuggestedRanges(
  totalLines: number,
  diagnostics: readonly DebugLogDiagnostic[],
): DebugLogSuggestedRange[] {
  if (totalLines === 0) return []
  const ranges = diagnostics
    .filter((diagnostic): diagnostic is DebugLogDiagnostic & {line: number} => diagnostic.line !== undefined)
    .slice(0, 10)
    .map(diagnostic => ({
      start_line: Math.max(1, diagnostic.line - RANGE_CONTEXT_LINES),
      end_line: Math.min(totalLines, diagnostic.line + RANGE_CONTEXT_LINES),
      reason: diagnostic.message,
    }))
  ranges.push({
    start_line: Math.max(1, totalLines - 49),
    end_line: totalLines,
    reason: 'Latest log activity',
  })
  return ranges
}

export function isImportantDebugLogSummary(summary: DebugLogSummary): boolean {
  if (summary.isError) return true
  return /^(chat\/(turnStarted|turnComplete|turnCancelled|error|toolCallStart|toolCallReady|toolCallConfirmed|toolCallComplete|pendingMessageSet|pendingMessageRemoved)|session\/(inputNeededSet|inputNeededRemoved)|ack)$/.test(
    summary.actionType ?? '',
  )
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

function stringField(record: Record<string, unknown>, field: string): string | undefined {
  const value = record[field]
  return typeof value === 'string' ? value : undefined
}

function numberField(record: Record<string, unknown>, field: string): number | undefined {
  const value = record[field]
  return typeof value === 'number' ? value : undefined
}

function compact(value: string | undefined): string | undefined {
  if (!value) return undefined
  const singleLine = value.replace(/\s+/g, ' ').trim()
  return singleLine.length > 80 ? `${singleLine.slice(0, 77)}...` : singleLine
}

function joinSummaryFields(fields: Array<string | false | undefined>): string {
  return fields.filter((field): field is string => Boolean(field)).join(' | ')
}
